import api from './api';

export interface DispatchRemoveReason {
  id: number;
  name: string;
  is_active: boolean;
}

export interface DispatchRemoveCategory {
  id: number;
  name: string;
  has_subcategories: boolean;
  is_active: boolean;
  reasons: DispatchRemoveReason[];
}

export interface CategoryCreate {
  name: string;
  has_subcategories: boolean;
  reasons?: { name: string }[];
}

export interface CategoryUpdate {
  name?: string;
  has_subcategories?: boolean;
  is_active?: boolean;
}

export interface ReasonCreate {
  name: string;
}

export interface ReasonUpdate {
  name?: string;
  is_active?: boolean;
}

export interface RemovalRecordItem {
  category_id: number;
  reason_id?: number | null;
  notes: string;
  pronumber: number;
  driver: string;
  dispatch_id?: number | null;
  appt_date?: string | null;
  deadline_type: 'LFD' | 'Per Diem' | 'Cutoff';
  deadline_date?: string | null;
}

class DispatchRemovalService {
  private baseUrl = '/dispatch-removal';

  async getCategories(includeInactive = false): Promise<DispatchRemoveCategory[]> {
    const response = await api.get<DispatchRemoveCategory[]>(
      `${this.baseUrl}/categories`,
      { params: { include_inactive: includeInactive } }
    );
    return response.data;
  }

  async createCategory(data: CategoryCreate): Promise<{ id: number; name: string }> {
    const response = await api.post(`${this.baseUrl}/categories`, data);
    return response.data;
  }

  async updateCategory(id: number, data: CategoryUpdate): Promise<{ success: boolean }> {
    const response = await api.put(`${this.baseUrl}/categories/${id}`, data);
    return response.data;
  }

  async getCategoryUsageCount(id: number): Promise<{ count: number }> {
    const response = await api.get<{ count: number }>(`${this.baseUrl}/categories/${id}/usage-count`);
    return response.data;
  }

  async createReason(categoryId: number, data: ReasonCreate): Promise<{ id: number; name: string }> {
    const response = await api.post(`${this.baseUrl}/categories/${categoryId}/reasons`, data);
    return response.data;
  }

  async updateReason(id: number, data: ReasonUpdate): Promise<{ success: boolean }> {
    const response = await api.put(`${this.baseUrl}/reasons/${id}`, data);
    return response.data;
  }

  async getReasonUsageCount(id: number): Promise<{ count: number }> {
    const response = await api.get<{ count: number }>(`${this.baseUrl}/reasons/${id}/usage-count`);
    return response.data;
  }

  async recordRemovals(items: RemovalRecordItem[]): Promise<{ success: boolean; count: number }> {
    const response = await api.post(`${this.baseUrl}/removals`, { items });
    return response.data;
  }
}

const dispatchRemovalService = new DispatchRemovalService();
export default dispatchRemovalService;
